import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tile, SkeletonText, Button } from '@carbon/react';
import {
  DataStructured,
  Document,
  Search,
  ChartNetwork,
  Filter,
  DataTable,
  ArrowRight,
  ChevronDown,
  ChevronRight,
} from '@carbon/icons-react';
import { useOntologies } from '../lib/query/hooks/useOntologies';
import { useKnowledgeBases } from '../lib/query/hooks/useKnowledgeBases';
import { useExplorations } from '../lib/query/hooks/useExplorations';
import { useDatasets } from '../lib/query/hooks/useDatasets';
import './KnowledgeManagementHomePage.scss';

/**
 * Knowledge Management Home Page
 *
 * Module-level landing page for the Knowledge Management module.
 * Shows key stats and explains the workflow:
 *   Knowledge Base → Ontology Builder → Knowledge Explorer (Graph · Facets · Datasets)
 */
export default function KnowledgeManagementHomePage() {
  const navigate = useNavigate();
  const [pipelineOpen, setPipelineOpen] = useState(false);

  const { data: ontologies, isLoading: loadingOntologies } = useOntologies();
  const { data: knowledgeBases, isLoading: loadingKBs } = useKnowledgeBases();
  const { data: explorations, isLoading: loadingExplorations } = useExplorations();
  const { data: datasets, isLoading: loadingDatasets } = useDatasets();

  // Aggregate document count from all KBs
  const documentCount = knowledgeBases
    ? knowledgeBases.reduce((sum, kb) => sum + (kb.knowledge_object_count ?? 0), 0)
    : undefined;

  const stats = [
    {
      label: 'Knowledge Bases',
      value: loadingKBs ? null : (knowledgeBases?.length ?? 0),
      icon: <Document size={24} />,
      color: 'orange',
      href: '/knowledge-bases',
    },
    {
      label: 'Documents',
      value: loadingKBs ? null : (documentCount ?? 0),
      icon: <Document size={24} />,
      color: 'yellow',
      href: '/knowledge-bases',
    },
    {
      label: 'Ontologies',
      value: loadingOntologies ? null : (ontologies?.length ?? 0),
      icon: <DataStructured size={24} />,
      color: 'blue',
      href: '/ontology-builder',
    },
    {
      label: 'Explorations',
      value: loadingExplorations ? null : (explorations?.length ?? 0),
      icon: <Search size={24} />,
      color: 'teal',
      href: '/explorer',
    },
    {
      label: 'Datasets',
      value: loadingDatasets ? null : (datasets?.length ?? 0),
      icon: <DataTable size={24} />,
      color: 'purple',
      href: '/datasets',
    },
  ];

  return (
    <div className="km-home">
      <div className="km-home__container">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <div className="km-home__hero">
          <h1 className="km-home__title">Knowledge Management</h1>
          <p className="km-home__subtitle">
            Build, structure, and explore scientific knowledge — from raw documents to a queryable knowledge graph.
          </p>
        </div>

        {/* ── Stats row ────────────────────────────────────────────────────── */}
        <div className="km-stats">
          {stats.map((stat) => (
            <Tile
              key={stat.label}
              className={`km-stat km-stat--${stat.color}`}
              onClick={() => navigate(stat.href)}
            >
              <div className={`km-stat__icon km-stat__icon--${stat.color}`}>
                {stat.icon}
              </div>
              <div className="km-stat__body">
                {stat.value === null ? (
                  <SkeletonText heading width="40%" />
                ) : (
                  <span className="km-stat__value">{stat.value}</span>
                )}
                <span className="km-stat__label">{stat.label}</span>
              </div>
            </Tile>
          ))}
        </div>

        {/* ── Workflow pipeline (collapsible) ──────────────────────────────── */}
        <div className="km-pipeline">
          <button
            type="button"
            className="km-pipeline__toggle"
            aria-expanded={pipelineOpen}
            onClick={() => setPipelineOpen((o) => !o)}
          >
            <h2 className="km-pipeline__heading">
              How the knowledge workflow works
            </h2>
            <span className="km-pipeline__toggle-hint">
              {pipelineOpen ? (
                <>
                  <ChevronDown size={16} />
                  <span>Collapse</span>
                </>
              ) : (
                <>
                  <ChevronRight size={16} />
                  <span>Expand to learn more</span>
                </>
              )}
            </span>
          </button>

          {pipelineOpen && (
            <div className="km-pipeline__flow">

              {/* Stage 1 — Knowledge Base */}
              <div className="km-pipeline-stage km-pipeline-stage--orange">
                <div className="km-pipeline-stage__icon km-pipeline-stage__icon--orange">
                  <Document size={28} />
                </div>
                <h3 className="km-pipeline-stage__title">Knowledge Base</h3>
                <p className="km-pipeline-stage__desc">
                  Ingest documents, papers, and datasets. The platform converts them into
                  searchable, chunked knowledge objects ready for AI extraction.
                </p>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={ArrowRight}
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate('/knowledge-bases'); }}
                >
                  Open Knowledge Bases
                </Button>
              </div>

              <div className="km-pipeline-arrow" aria-hidden="true">
                <ArrowRight size={24} />
              </div>

              {/* Stage 2 — Ontology Builder */}
              <div className="km-pipeline-stage km-pipeline-stage--blue">
                <div className="km-pipeline-stage__icon km-pipeline-stage__icon--blue">
                  <DataStructured size={28} />
                </div>
                <h3 className="km-pipeline-stage__title">Ontology Builder</h3>
                <p className="km-pipeline-stage__desc">
                  Define the vocabulary of your domain — concepts, relationships, and
                  hierarchies — either manually or with AI generation from your documents.
                </p>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={ArrowRight}
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate('/ontology-builder'); }}
                >
                  Open Ontology Builder
                </Button>
              </div>

              <div className="km-pipeline-arrow" aria-hidden="true">
                <ArrowRight size={24} />
              </div>

              {/* Stage 3 — Knowledge Explorer */}
              <div className="km-pipeline-stage km-pipeline-stage--teal">
                <div className="km-pipeline-stage__icon km-pipeline-stage__icon--teal">
                  <Search size={28} />
                </div>
                <h3 className="km-pipeline-stage__title">Knowledge Explorer</h3>
                <p className="km-pipeline-stage__desc">
                  Ground your ontology against the knowledge base to produce a queryable
                  A-Box knowledge graph. Explore it through three complementary views:
                </p>
                <div className="km-pipeline-stage__outputs">
                  <div className="km-pipeline-output">
                    <ChartNetwork size={16} />
                    <span>Graph — visual exploration of concept instances and evidence</span>
                  </div>
                  <div className="km-pipeline-output">
                    <Filter size={16} />
                    <span>Facets — faceted search over entities, claims, and semantics</span>
                  </div>
                  <div className="km-pipeline-output">
                    <DataTable size={16} />
                    <span>Datasets — curated, structured tables exported from the graph</span>
                  </div>
                </div>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={ArrowRight}
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate('/explorer'); }}
                >
                  Open Knowledge Explorer
                </Button>
              </div>

            </div>
          )}
        </div>

        {/* ── Module cards ─────────────────────────────────────────────────── */}
        <div className="km-modules">
          <h2 className="km-modules__heading">Modules</h2>
          <div className="km-modules__grid">

            <Tile
              className="km-module-card km-module-card--orange"
              onClick={() => navigate('/knowledge-bases')}
            >
              <div className="km-module-card__icon km-module-card__icon--orange">
                <Document size={32} />
              </div>
              <h3 className="km-module-card__title">Knowledge Base</h3>
              <p className="km-module-card__desc">
                Upload and manage source documents. Track conversion, chunking, and
                embedding progress for every knowledge object.
              </p>
              <span className="km-module-card__cta">
                Go to Knowledge Base <ArrowRight size={16} />
              </span>
            </Tile>

            <Tile
              className="km-module-card km-module-card--blue"
              onClick={() => navigate('/ontology-builder')}
            >
              <div className="km-module-card__icon km-module-card__icon--blue">
                <DataStructured size={32} />
              </div>
              <h3 className="km-module-card__title">Ontology Builder</h3>
              <p className="km-module-card__desc">
                Build, version, and share domain ontologies. Start from scratch,
                generate with AI, or extract from your knowledge base.
              </p>
              <span className="km-module-card__cta">
                Go to Ontology Builder <ArrowRight size={16} />
              </span>
            </Tile>

            <Tile
              className="km-module-card km-module-card--teal"
              onClick={() => navigate('/explorer')}
            >
              <div className="km-module-card__icon km-module-card__icon--teal">
                <Search size={32} />
              </div>
              <h3 className="km-module-card__title">Knowledge Explorer</h3>
              <p className="km-module-card__desc">
                Populate ontologies with evidence from your knowledge base and explore
                the resulting knowledge graph via Graph, Facets, and Datasets views.
              </p>
              <span className="km-module-card__cta">
                Go to Knowledge Explorer <ArrowRight size={16} />
              </span>
            </Tile>

          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <footer className="km-footer">
          <p className="km-footer__text">
            AI-First Scientific Workflow Platform
          </p>
          <p className="km-footer__text">
            Made with Scientific ❤️ by IBM Science Studio team
          </p>
          <p className="km-footer__powered-by">
            Powered by&nbsp;
            <img
              src="https://docling-project.github.io/docling/assets/logo.png"
              alt="Docling logo"
              className="km-footer__docling-logo"
            />
            &nbsp;<a
              href="https://github.com/docling-project/docling"
              target="_blank"
              rel="noopener noreferrer"
              className="km-footer__powered-link"
            >docling</a>
            &nbsp;and&nbsp;
            <a
              href="https://github.com/docling-project/docling-graph"
              target="_blank"
              rel="noopener noreferrer"
              className="km-footer__powered-link"
            >docling-graph</a>
          </p>
          <p className="km-footer__disclaimer">
            This demo version is provided as-is, without warranties of any kind, express or implied.
            IBM assumes no liability for any use of this software or its outputs.
          </p>
        </footer>

      </div>
    </div>
  );
}

// Made with Bob
